import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, BookOpen, Camera, FlaskConical, Pencil, ScanText, Stethoscope, Trash2, Wrench } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { formulairePhoto, reduirePhoto } from '../../lib/photo.ts';
import { BoutonPhoto, useLecturePhoto, type Plaque } from '../../components/LecturePhoto.tsx';

/** Nombre maximal de photos par équipement (contrôlé aussi côté serveur). */
const PHOTOS_MAX = 12;
import { useSession } from '../../lib/session.tsx';
import { date, dateHeure } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, ChampChoix, ChampTexte, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, FilContexte, LienBouton, Panneau, Valeur,
  useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { CarnetEquipement } from '../../components/Carnet.tsx';
import { BadgeStatutDocument } from '../../components/statuts.tsx';
import { FormulaireEquipement, type EquipementSaisie, type Lieu, type Systeme } from './FormulaireEquipement.tsx';
import { BoutonHorsLigne } from '../../components/BoutonHorsLigne.tsx';
import { SectionPannesEquipement } from '../interventions/PanneauDejaVu.tsx';

interface Fiche extends EquipementSaisie {
  id: string; site_id: string; created_at: string; updated_at: string; photos: { id: string }[];
  site: { id: string; name: string; demo: boolean };
  chemin: { id: string; kind: string; name: string }[];
  systeme: { id: string; name: string; domain: string } | null;
  interventions: { id: string; reference: string; title: string; status: string; created_at: string }[];
  documents: { version_id: string; document_id: string; title: string; doc_type: string; version_label: string; status: string;
    versions_covered: string | null; correspondance: 'reference_exacte' | 'gamme' }[];
  /** Équipement archivé (masqué des listes, fiche figée, carnet lisible). */
  archive: { le: string; par: string | null; motif: string; remplace_par: { id: string; label: string } | null } | null;
  /** Équipements que celui-ci a remplacés. */
  remplace: { id: string; label: string; archived_at: string }[];
  /** Posé sur un chantier (carnet de chantier, incrément 5) : la dernière pose, avec la page du jour. */
  pose: { chantier: { id: string; reference: string | null; titre: string }; jour: string; remplace: string | null } | null;
  /** Sans historique : une fiche saisie par erreur peut être supprimée ; sinon elle s'archive. */
  supprimable: boolean;
}

const TYPES_LIEU: Record<string, string> = { batiment: 'Bâtiment', etage: 'Étage', zone: 'Zone' };

export function PageEquipement() {
  const { id } = useParams();
  const { peut } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const [edition, setEdition] = useState(false);
  const [proposition, setProposition] = useState<Plaque | null>(null);
  const [archivage, setArchivage] = useState(false);
  const { hash } = useLocation();
  const q = useQuery({ queryKey: ['equipement', id], queryFn: () => api.get<Fiche>(`/api/equipements/${id}`) });
  const site = useQuery({
    queryKey: ['site', q.data?.site_id], enabled: edition && !!q.data,
    queryFn: () => api.get<{ lieux: Lieu[]; systemes: Systeme[] }>(`/api/sites/${q.data!.site_id}`),
  });

  // « Tout le carnet » depuis l'espace de diagnostic : défilement jusqu'au carnet une fois la fiche chargée.
  useEffect(() => {
    if (hash === '#carnet' && q.data) document.getElementById('carnet')?.scrollIntoView({ block: 'start' });
  }, [hash, !!q.data]);

  if (q.isLoading) return <Chargement lignes={6} />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const e = q.data;
  const exacts = e.documents.filter((d) => d.correspondance === 'reference_exacte');
  const gamme = e.documents.filter((d) => d.correspondance === 'gamme');

  const archive = e.archive;
  const ecrire = peut('site.ecrire') && !archive;
  const supprimer = async () => {
    if (!(await confirmer(`Supprimer « ${e.label} » ?`, 'Cette fiche n’a aucun historique (saisie erronée) : elle sera retirée du site définitivement.', 'Supprimer l’équipement'))) return;
    try { await api.del(`/api/equipements/${e.id}`); notifier('Équipement supprimé.'); qc.invalidateQueries({ queryKey: ['site', e.site_id] }); nav(`/sites/${e.site_id}`); }
    catch (err) { notifier(messageErreur(err), true); }
  };
  const restaurer = async () => {
    if (!(await confirmer(`Restaurer « ${e.label} » ?`, 'Il réapparaîtra dans les listes du site et redeviendra modifiable. Le lien « remplacé par » sera retiré.', 'Restaurer l’équipement'))) return;
    try { await api.post(`/api/equipements/${e.id}/restaurer`); notifier('Équipement restauré.'); qc.invalidateQueries({ queryKey: ['equipement'] }); qc.invalidateQueries({ queryKey: ['site', e.site_id] }); qc.invalidateQueries({ queryKey: ['carnet'] }); }
    catch (err) { notifier(messageErreur(err), true); }
  };

  const champs: [string, ReactNode][] = [
    ['Fabricant', <Valeur v={e.manufacturer} />],
    ['Gamme', <Valeur v={e.range_name} />],
    ['Référence exacte', <Valeur v={e.reference} mono />],
    ['Catégorie', <Valeur v={e.category} inconnu="Non précisée" />],
    ['Version matérielle', <Valeur v={e.hw_version} mono />],
    ['Version logicielle', <Valeur v={e.sw_version} mono />],
    ['Firmware', <Valeur v={e.firmware} mono />],
    ['Protocoles', <Valeur v={e.protocols} />],
    ['Adresse réseau / bus', <Valeur v={e.network_address} mono />],
    ['N° de série', <Valeur v={e.serial_number} mono />],
    ['Mise en service', <Valeur v={e.installed_on ? date(e.installed_on) : null} />],
    ['Système', <Valeur v={e.systeme?.name} inconnu="Aucun" />],
  ];

  return (
    <div className="pile-l">
      <EntetePage
        fil={<FilContexte noeuds={[{ type: 'Site', libelle: e.site.name, to: `/sites/${e.site.id}` },
          ...e.chemin.map((c) => ({ type: TYPES_LIEU[c.kind], libelle: c.name, to: `/sites/${e.site.id}` })),
          { type: 'Équipement', libelle: e.label }]} />}
        titre={<span className="rangee" style={{ gap: 10 }}>{e.label}{e.site.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
          {archive && <Badge ton="neutre" icone={<Archive size={14} aria-hidden />}>Archivé</Badge>}</span>}
        sousTitre={[e.manufacturer, e.range_name].filter(Boolean).join(' — ') || 'Fabricant inconnu'}
        actions={<>
          {peut('intervention.ecrire') && !archive && <LienBouton to={`/interventions/nouvelle?equipement=${e.id}`} variante="principal" icone={<Stethoscope aria-hidden />}>Diagnostiquer cet équipement</LienBouton>}
          {ecrire && <Bouton icone={<Pencil aria-hidden />} onClick={() => setEdition(true)}>Modifier</Bouton>}
          <BoutonHorsLigne cle={`equipement:${e.id}`} type="equipement" libelle={`${e.label} — ${e.site.name}`}
            urls={[`/api/equipements/${e.id}`, `/api/equipements/${e.id}/pannes-resolues`, `/api/equipements/${e.id}/carnet`, ...e.documents.filter((d) => d.status === 'valide').flatMap((d) => [`/api/documents/versions/${d.version_id}`, `/api/documents/versions/${d.version_id}/fichier`])]} />
        </>} />

      {archive && (
        <div className="pile" style={{ gap: 'var(--e-2)' }}>
        <Alerte ton="info" titre={`Équipement archivé le ${dateHeure(archive.le)}${archive.par ? ` par ${archive.par}` : ''}`}>
          <span>Motif : {archive.motif}. </span>
          {archive.remplace_par && <span>Remplacé par <Link to={`/sites/equipements/${archive.remplace_par.id}`}>{archive.remplace_par.label}</Link>. </span>}
          <span>La fiche est figée ; son carnet reste consultable.</span>
        </Alerte>
        {peut('site.ecrire') && <div><Bouton icone={<ArchiveRestore aria-hidden />} onClick={restaurer}>Restaurer l’équipement</Bouton></div>}
        </div>
      )}
      {e.pose && (
        <p className="petit secondaire eq-pose">
          <Wrench size={14} aria-hidden /> Posé sur le chantier <Link to={`/interventions/chantiers/${e.pose.chantier.id}?onglet=pieces`}>{e.pose.chantier.reference ?? e.pose.chantier.titre}</Link> le {date(e.pose.jour)}
          {e.pose.remplace ? `, en remplacement de « ${e.pose.remplace} »` : ''}. <Link to={`/interventions/chantiers/${e.pose.chantier.id}/jour/${e.pose.jour}`}>Voir la page du jour</Link>
        </p>
      )}
      {e.remplace.length > 0 && (
        <p className="petit secondaire">Remplace :{e.remplace.map((x, k) => <span key={x.id}>{k ? ', ' : ''}<Link to={`/sites/equipements/${x.id}`}>{x.label}</Link> (archivé le {date(x.archived_at)})</span>)}.</p>
      )}
      {!archive && (!e.reference || !e.manufacturer) && (
        <Alerte ton="ambre" titre="Identification incomplète">
          Sans fabricant et référence exacte, la documentation ne peut pas être rattachée avec certitude. Relevez la plaque signalétique lors de la prochaine visite.
        </Alerte>
      )}

      <div className="grille-2 eq-disposition">
        <div className="pile-l eq-colonne">
          <div className="eq-o-ident">
            <Panneau titre="Identification">
              <dl className="fiche">
                {champs.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
              </dl>
              {e.notes && <><hr className="separateur" style={{ margin: '16px 0' }} /><p className="petit" style={{ whiteSpace: 'pre-wrap' }}>{e.notes}</p></>}
            </Panneau>
          </div>
          <div className="eq-o-photos">
            <PanneauPhotos equipement={e} ecrire={ecrire} rafraichir={() => q.refetch()}
              plaqueLue={(p) => { setProposition(p); setEdition(true); }} />
          </div>
        </div>

        <div className="pile-l eq-colonne">
          <div className="eq-o-carnet"><CarnetEquipement equipementId={e.id} /></div>

          <div className="eq-o-docs">
            <Panneau serre titre="Documentation rattachée" actions={<LienBouton compact variante="fantome" to={`/documentation?ref=${encodeURIComponent(e.reference ?? e.range_name ?? '')}`} icone={<BookOpen aria-hidden />}>Bibliothèque</LienBouton>}>
              {e.documents.length === 0
                ? <EtatVide titre="Aucun document pour cette référence"
                    texte={e.reference ? <>Aucun document de la bibliothèque ne cite <span className="mono">{e.reference}</span>. Importez la notice ou la fiche produit du fabricant.</>
                      : 'Renseignez la référence exacte pour retrouver la documentation.'}
                    action={peut('doc.importer') ? <LienBouton compact to="/documentation/importer">Importer un document</LienBouton> : undefined} />
                : <div className="liste">
                  {[...exacts, ...gamme].map((d) => (
                    <Link key={d.version_id} to={`/documentation/lecteur/${d.version_id}`} className="liste-element">
                      <span className="liste-element-corps">
                        <span className="liste-element-titre">{d.title}</span>
                        <span className="petit discret">Version {d.version_label}{d.versions_covered ? ` — couvre : ${d.versions_covered}` : ''}</span>
                      </span>
                      <Badge ton={d.correspondance === 'reference_exacte' ? 'accent' : 'neutre'} icone={false}>{d.correspondance === 'reference_exacte' ? 'Référence exacte' : 'Même gamme'}</Badge>
                      <BadgeStatutDocument statut={d.status} />
                    </Link>
                  ))}
                </div>}
            </Panneau>
          </div>

          <div className="eq-o-pannes"><SectionPannesEquipement equipementId={e.id} /></div>
          {ecrire && (
            <div className="eq-o-actions groupe-btn">
              {e.supprimable
                ? <Bouton variante="danger" icone={<Trash2 aria-hidden />} onClick={supprimer}>Supprimer l’équipement</Bouton>
                : <Bouton icone={<Archive aria-hidden />} onClick={() => setArchivage(true)}>Archiver l’équipement</Bouton>}
              {!e.supprimable && <span className="tres-petit discret">Il a un historique : il s’archive au lieu d’être supprimé.</span>}
            </div>
          )}
        </div>
      </div>

      {edition && site.data && (
        <FormulaireEquipement ouvert fermer={() => { setEdition(false); setProposition(null); }} siteId={e.site_id} lieux={site.data.lieux} systemes={site.data.systemes} initial={e}
          proposition={proposition} photoAjoutee={() => q.refetch()}
          enregistre={() => { setEdition(false); setProposition(null); notifier('Équipement mis à jour.'); qc.invalidateQueries({ queryKey: ['equipement', id] }); qc.invalidateQueries({ queryKey: ['site', e.site_id] }); }} />
      )}
      {archivage && <DialogueArchivage equipement={e} fermer={() => setArchivage(false)}
        fait={() => { setArchivage(false); notifier('Équipement archivé.'); qc.invalidateQueries({ queryKey: ['equipement'] }); qc.invalidateQueries({ queryKey: ['site', e.site_id] }); qc.invalidateQueries({ queryKey: ['carnet'] }); }} />}
      {confirmation}
    </div>
  );
}

/** Archivage : motif obligatoire ; « remplacé par » un équipement du MÊME site (facultatif). */
function DialogueArchivage({ equipement: e, fermer, fait }: { equipement: Fiche; fermer: () => void; fait: () => void }) {
  const [motif, setMotif] = useState('');
  const [remplacant, setRemplacant] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const site = useQuery({ queryKey: ['site', e.site_id], queryFn: () => api.get<{ equipements: { id: string; label: string; reference: string | null }[] }>(`/api/sites/${e.site_id}`) });
  const choix = (site.data?.equipements ?? []).filter((x) => x.id !== e.id);
  const valider = async () => {
    if (motif.trim().length < 3) { setErreur('Indiquez le motif de l’archivage.'); return; }
    setEnvoi(true); setErreur(null);
    try { await api.post(`/api/equipements/${e.id}/archiver`, { motif: motif.trim(), remplaceParId: remplacant || null }); fait(); }
    catch (err) { setErreur(messageErreur(err)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={`Archiver « ${e.label} »`} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} icone={<Archive aria-hidden />} onClick={valider}>Archiver</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire">L’équipement sera masqué des listes du site. Sa fiche, ses diagnostics et son carnet restent consultables ; rien n’est effacé.</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampTexte libelle="Motif" rows={3} maxLength={1000} value={motif} onChange={(x) => setMotif(x.target.value)}
          placeholder="ex. Remplacé lors du dépannage, hors service, déposé" />
        <ChampChoix libelle="Remplacé par (facultatif)" value={remplacant} onChange={(x) => setRemplacant(x.target.value)} vide="Aucun"
          options={choix.map((x) => ({ valeur: x.id, libelle: `${x.label}${x.reference ? ` — ${x.reference}` : ''}` }))}
          aide={site.isLoading ? 'Chargement des équipements du site…' : 'Équipement de ce site qui prend sa place. Le lien apparaîtra dans les deux carnets.'} />
      </div>
    </Dialogue>
  );
}

/** Photos de l'équipement : vignettes, ajout (appareil photo du téléphone), lecture de la plaque, suppression confirmée. */
function PanneauPhotos({ equipement: e, ecrire, rafraichir, plaqueLue }: {
  equipement: Fiche; ecrire: boolean; rafraichir: () => void; plaqueLue: (p: Plaque) => void;
}) {
  const lp = useLecturePhoto();
  const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const [envoi, setEnvoi] = useState<'ajout' | 'plaque' | null>(null);
  const [lecture, setLecture] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const idExplication = useId();
  const plein = e.photos.length >= PHOTOS_MAX;
  const texte = (err: unknown) => (err instanceof ErreurApi ? err.message : err instanceof Error ? err.message : messageErreur(err));

  const lirePlaque = async (pid: string) => {
    setLecture(pid); setErreur(null);
    try { plaqueLue((await api.post<{ plaque: Plaque }>(`/api/equipements/${e.id}/photos/${pid}/lire-plaque`)).plaque); }
    catch (err) { setErreur(texte(err)); } finally { setLecture(null); }
  };
  const ajouter = async (fichier: File, lire: boolean) => {
    setEnvoi(lire ? 'plaque' : 'ajout'); setErreur(null);
    try {
      const photo = await reduirePhoto(fichier);
      const { id } = await api.envoyer<{ id: string }>(`/api/equipements/${e.id}/photos`, formulairePhoto(photo));
      rafraichir();
      if (lire) await lirePlaque(id); else notifier('Photo ajoutée.');
    } catch (err) { setErreur(texte(err)); } finally { setEnvoi(null); }
  };
  const supprimer = async (pid: string, n: number) => {
    if (!(await confirmer(`Supprimer la photo ${n} ?`, 'Elle sera retirée de la fiche de l’équipement. Cette action est définitive.', 'Supprimer la photo'))) return;
    try { await api.del(`/api/equipements/${e.id}/photos/${pid}`); notifier('Photo supprimée.'); rafraichir(); }
    catch (err) { notifier(messageErreur(err), true); }
  };

  return (
    <Panneau titre="Photos">
      <div className="pile">
        {ecrire && (
          <div className="photo-actions">
            <BoutonPhoto variante="principal" icone={<ScanText aria-hidden />} surPhoto={(f) => ajouter(f, true)} chargement={envoi === 'plaque'}
              desactive={!lp.possible || !!envoi || plein} decritPar={lp.raison ? idExplication : undefined}>Photographier la plaque</BoutonPhoto>
            <BoutonPhoto icone={<Camera aria-hidden />} surPhoto={(f) => ajouter(f, false)} chargement={envoi === 'ajout'}
              desactive={!lp.enLigne || !!envoi || plein} decritPar={!lp.enLigne ? idExplication : undefined}>Ajouter une photo</BoutonPhoto>
          </div>
        )}
        {ecrire && lp.raison && <p id={idExplication} className="tres-petit discret">{lp.raison}</p>}
        {ecrire && plein && <p className="tres-petit discret">{PHOTOS_MAX} photos au maximum : supprimez-en une pour en ajouter.</p>}
        {erreur && <Alerte ton="danger" titre="Photo">{erreur}</Alerte>}
        {e.photos.length === 0
          ? <p className="petit discret">Aucune photo. Une photo de la plaque signalétique aide à identifier l’équipement sans erreur.</p>
          : (
            <ul className="photo-grille" aria-label="Photos de l’équipement">
              {e.photos.map((p, i) => {
                const url = `/api/equipements/${e.id}/photos/${p.id}`;
                return (
                  <li key={p.id} className="photo-vignette">
                    <a href={url} target="_blank" rel="noopener" className="photo-lien" aria-label={`Ouvrir la photo ${i + 1} en grand`}>
                      <img src={url} alt={`Photo ${i + 1} de ${e.label}`} loading="lazy" />
                    </a>
                    {ecrire && (
                      <div className="photo-actions-vignette">
                        <Bouton icone={<ScanText aria-hidden />} chargement={lecture === p.id} disabled={!lp.possible || !!lecture || !!envoi}
                          aria-describedby={lp.raison ? idExplication : undefined} onClick={() => lirePlaque(p.id)}>Lire la plaque</Bouton>
                        <Bouton variante="fantome" className="btn-icone" aria-label={`Supprimer la photo ${i + 1}`} disabled={!lp.enLigne}
                          onClick={() => supprimer(p.id, i + 1)}><Trash2 aria-hidden /></Bouton>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
      </div>
      {element}
    </Panneau>
  );
}
